/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState, useCallback, FC, Fragment } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Paper, IconButton, LinearProgress, Grid } from "@material-ui/core";
import { useDispatch, useStore } from "react-redux";
import { RootState, RootDispatch } from "../redux/store";
import { getMimeType, UNKNOWN_FILETYPE, formatFileSize } from "../utils/filetypes";
import { DocumentState } from "../clients/FirebaseClient/types";
import config from "../config";
import moment from "moment";
import { Colors } from "../styles";
import { Button } from "./core/Button";
import { defaultFirebaseClient } from "../clients/FirebaseClient";

import InfoIcon from "../assets/images/info_icon.svg";
import UploadIcon from "../assets/images/upload_icon.svg";
import DownloadIcon from "../assets/images/download_icon.svg";
import DeleteIcon from "../assets/images/delete_icon.svg";
import { ConfigurationActions } from "../redux";

const useStyles = makeStyles(() =>
    createStyles({
        componentFrame: {
            display: "flex",
            flexDirection: "column",
            padding: 24,
            fontFamily: "innogyWEB",
            borderRadius: 8,
            marginBottom: 24,
        },
        title: {
            margin: 0,
            fontFamily: "innogyWEB",
            fontSize: 25,
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.28,
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        description: {
            marginTop: 9,
            left: 24,
            marginBottom: 38,
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontWeight: 300,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.27,
            letterSpacing: "normal",
            color: Colors.anthracite,
        },
        yourDocumentsLabel: {
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: "normal",
            letterSpacing: "normal",
            color: Colors.anthracite,
            marginTop: 0,
        },
        sizeLabel: {
            margin: 0,
        },
        formatLabel: {
            margin: 0,
        },
        dropArea: {
            display: "flex",
            flexDirection: "column",
            borderRadius: 8,
            border: `dashed 2px ${Colors.anthracite25}`,
            justifyContent: "space-around",
            alignItems: "center",
            backgroundColor: "white",
            marginTop: 12,
            height: 146,
        },
        dropAreaActive: {
            borderRadius: 8,
            border: `solid 1px ${Colors.blueBright}`,
            backgroundColor: Colors.eggshell,
            marginTop: 13,
            marginBottom: 1,
        },
        button: {
            background: Colors.mellowYellow,
            color: Colors.white,
            borderRadius: 30,
            width: 227,
            height: 45,
            marginBottom: 10,
            "&:hover": {
                background: Colors.pumpkin,
            },
        },
        buttonText: {
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.27,
            letterSpacing: "normal",
            textAlign: "center",
            color: "#ffffff",
            textTransform: "none",
        },
        retryButton: {
            height: 56,
            width: 240,
            borderRadius: 28,
            background: Colors.anthracite5,
            textTransform: "none",
        },
        retryButtonText: {
            fontFamily: "innogyWEB",
            fontSize: 22,
            fontWeight: 500,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.27,
            letterSpacing: "normal",
            textAlign: "center",
            color: Colors.anthracite,
        },
        dropText: {
            color: Colors.anthracite,
            marginBlockStart: 0,
            marginBlockEnd: 0,
            fontSize: 22,
        },
        uploadProgress: {
            flex: 1,
            backgroundColor: "#fff",
            borderRadius: 8,
            color: Colors.blueMuted,
        },
        fileLine: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 22,
            color: "#3c3732",
            fontWeight: 300,
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.27,
            letterSpacing: "normal",
        },
        icon: {
            width: 30,
            height: 30,
            objectFit: "contain",
        },
        progressSpan: {
            display: "flex",
            width: 250,
            marginLeft: 25,
            alignItems: "center",
        },
        errorSpan: {
            color: "#eb4b0a",
            display: "flex",
            flexDirection: "row",
            alignItems: "start",
            paddingTop: 38,
        },
    }),
);

interface OwnProps {
    document?: DocumentState;
    classification: string;
}

type SamplePlanMappingsType = {
    [key in string]: {
        description: string;
        url: string;
    };
};

const samplePlanMappings: SamplePlanMappingsType = {
    crossSectionPlan: {
        description: "Beispiel-Gebäudequerschnittsplan",
        url: `${process.env.PUBLIC_URL}/samples/QuerschnittMuster.pdf`,
    },
    floorPlan: {
        description: "Beispiel-Geschossplan",
        url: `${process.env.PUBLIC_URL}/samples/GeschossplanMuster.pdf`,
    },
    sitePlan: {
        description: "Beispiel-Lageplan",
        url: `${process.env.PUBLIC_URL}/samples/LageplanMuster.pdf`,
    },
};

export const DocumentUpload: FC<OwnProps> = (props: OwnProps) => {
    const classes = useStyles();
    const [error, setError] = useState<boolean>(
        !!(props.document?.relevant && props.document?.error?.message),
    );

    const dispatch: RootDispatch = useDispatch();
    const store = useStore<RootState>();

    const acceptCallback = useCallback(
        async (files: File[]) => {
            const fileType = await getMimeType(files[0]);
            if (fileType === UNKNOWN_FILETYPE) {
                setError(true);
            } else {
                dispatch(
                    ConfigurationActions.documentUpload(
                        store.getState().auth.uid!,
                        props.classification,
                        store.getState().configuration.currentConfigurationId!,
                        files[0],
                    ),
                );
            }
        },
        [dispatch, props.classification, store],
    );

    const rejectCallback = useCallback((rejections: FileRejection[]) => {
        if (rejections[0].file.size > config.uploads.maxSize) {
            setError(true);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        maxSize: config.uploads.maxSize,
        onDropAccepted: acceptCallback,
        onDropRejected: rejectCallback,
    });

    if (!props.document?.relevant) return <span data-message="document is not relevant" />;

    const dropClass = isDragActive
        ? [classes.dropArea, classes.dropAreaActive].join(" ")
        : classes.dropArea;

    const fileUploadSpec = (
        <React.Fragment>
            <p className={classes.sizeLabel}>
                Die maximal zulässige Dateigröße beträgt&nbsp;
                {formatFileSize(config.uploads.maxSize)}
            </p>
            <p className={classes.formatLabel}>
                Unterstützte Formate: {config.uploads.extensions.join(", ")}
            </p>
        </React.Fragment>
    );

    const samplePlanMapping = samplePlanMappings[props.classification];

    return (
        <Grid item>
            <Paper elevation={2} className={classes.componentFrame}>
                <h3 className={classes.title}>{props.document.title}</h3>
                {samplePlanMapping ? (
                    <div className={classes.fileLine}>
                        <span style={{ color: Colors.mellowYellow }}>
                            <IconButton
                                style={{ marginLeft: -12 }}
                                data-testid="download-button"
                                onClick={async () => window.open(samplePlanMapping.url, "_blank")}
                            >
                                <img alt="" className={classes.icon} src={DownloadIcon} />
                            </IconButton>
                            {samplePlanMapping.description}
                        </span>
                    </div>
                ) : (
                    <Fragment />
                )}
                {error ? (
                    <React.Fragment>
                        <div className={classes.description}>
                            <div>{props.document.description}</div>
                            <span className={classes.errorSpan}>
                                <img alt="" src={InfoIcon} />
                                <span style={{ paddingLeft: 16 }}>
                                    <div>
                                        Das Hochladen ihrer Datei ist fehlgeschlagen. Bitte
                                        wiederholen Sie den Vorgang. Bitte prüfen Sie das Format
                                        Ihrer Datei und die Dateigröße
                                    </div>
                                    {fileUploadSpec}
                                </span>
                            </span>
                        </div>
                        <Button
                            mode="primary"
                            variant="contained"
                            className={classes.retryButton}
                            onClick={() => setError(false)}
                        >
                            <span className={classes.retryButtonText}>Erneut versuchen</span>
                        </Button>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <div className={classes.description}>
                            <div>{props.document.description}</div>
                            <span>{fileUploadSpec}</span>
                            {props.document.progress && props.document.progress < 0 ? (
                                <span className={classes.errorSpan}>
                                    <img alt="" src={InfoIcon} />
                                    <span style={{ paddingLeft: 16 }}>
                                        <div style={{ fontWeight: "normal" }}>
                                            Das Hochladen der Datei dauert ungewöhnlich lange!
                                        </div>
                                        <div>
                                            Bitte überprüfen Sie Ihre Netzwerkverbindung und stellen
                                            Sie sicher, dass die Adresse
                                            https://firebasestorage.googleapis.com nicht blockiert
                                            wird. Setzen Sie sich ggf. mit Ihrem
                                            Netzwerkadministrator in Verbindung. Alternativ stehen
                                            Ihnen unsere Anforderungsformulare zur Beantragung Ihres
                                            Netzanschlusses{" "}
                                            <a
                                                href="https://www.syna.de/corp/netzanschluss/ihr-weg-zum-netzanschluss"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                hier zur Verfügung
                                            </a>
                                            .
                                        </div>
                                    </span>
                                </span>
                            ) : (
                                <Fragment />
                            )}
                        </div>
                        {props.document.files.length ? (
                            <h3 className={classes.yourDocumentsLabel}>Ihre Dokumente</h3>
                        ) : (
                            <React.Fragment />
                        )}
                        {props.document.files.map((file, idx) => (
                            <div key={file.objectId} className={classes.fileLine}>
                                {props.document?.relevant &&
                                props.document.progress &&
                                props.document.files.length - 1 === idx ? (
                                    <React.Fragment>
                                        <span>
                                            <IconButton style={{ marginLeft: -12 }}>
                                                <img
                                                    alt=""
                                                    className={classes.icon}
                                                    src={UploadIcon}
                                                />
                                            </IconButton>
                                            {file.userFilename} ({file.extension},{" "}
                                            {formatFileSize(file.size)})
                                        </span>
                                        <span className={classes.progressSpan}>
                                            <LinearProgress
                                                className={classes.uploadProgress}
                                                variant="determinate"
                                                value={props.document.progress}
                                            />
                                            <IconButton
                                                style={{ marginRight: -12 }}
                                                onClick={() =>
                                                    dispatch(
                                                        ConfigurationActions.documentCancelUpload(),
                                                    )
                                                }
                                            >
                                                <img
                                                    alt=""
                                                    style={{ height: 24, width: 24 }}
                                                    className={classes.icon}
                                                    src={DeleteIcon}
                                                />
                                            </IconButton>
                                        </span>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <span style={{ color: Colors.mellowYellow }}>
                                            <IconButton
                                                style={{ marginLeft: -12 }}
                                                data-testid="download-button"
                                                onClick={async () =>
                                                    (window.location.href = await defaultFirebaseClient.getDownloadUrl(
                                                        file.objectId,
                                                    ))
                                                }
                                            >
                                                <img
                                                    alt=""
                                                    className={classes.icon}
                                                    src={DownloadIcon}
                                                />
                                            </IconButton>
                                            {file.userFilename} ({file.extension},{" "}
                                            {formatFileSize(file.size)})
                                        </span>
                                        <span>
                                            <span>
                                                {moment
                                                    .unix(file.createdAt)
                                                    .format("DD.MM.Y, HH:mm U\\hr")}
                                            </span>
                                            <IconButton
                                                style={{ marginRight: -12 }}
                                                onClick={() =>
                                                    dispatch(
                                                        ConfigurationActions.documentRemove(
                                                            file.objectId,
                                                        ),
                                                    )
                                                }
                                            >
                                                <img
                                                    alt=""
                                                    style={{ height: 24, width: 24 }}
                                                    data-testid="delete-button"
                                                    className={classes.icon}
                                                    src={DeleteIcon}
                                                />
                                            </IconButton>
                                        </span>
                                    </React.Fragment>
                                )}
                            </div>
                        ))}
                        {props.document.progress ? (
                            <React.Fragment />
                        ) : (
                            <div className={dropClass} {...getRootProps()}>
                                <Button
                                    mode="primary"
                                    variant="contained"
                                    className={classes.button}
                                >
                                    <span className={classes.buttonText}>Datei hochladen</span>
                                </Button>
                                <div className={classes.dropText}>oder hier ablegen</div>
                                <input
                                    type="file"
                                    id="file"
                                    name="file"
                                    {...getInputProps()}
                                ></input>
                            </div>
                        )}
                    </React.Fragment>
                )}
            </Paper>
        </Grid>
    );
};
